import PropTypes from 'prop-types';
import { forwardRef } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
// routes
import { RouterLink } from 'src/routes/components';
// hooks
import { useAuth } from 'src/hooks/useAuth';
import { useSiteSettings } from 'src/hooks/use-site-settings';

// ----------------------------------------------------------------------

const Logo = forwardRef(({ disabledLink = false, variant = 'full', sx, ...other }, ref) => {
  const { isAuthenticated } = useAuth();
  const { settings: siteSettings } = useSiteSettings();
  const theme = useTheme();

  // Determine logo source
  let logoSrc;
  if (variant === 'small') {
    logoSrc = isAuthenticated && siteSettings?.icon 
      ? siteSettings.icon 
      : '/logo/logo_single.svg';
  } else {
    logoSrc = isAuthenticated && siteSettings?.logo 
      ? siteSettings.logo 
      : '/logo/logo_full.svg';
  }

  const logoWidth = variant === 'small' ? 40 : 150;
  const logoHeight = 40;
  const primaryColor = theme.palette.primary.main;

  // Only apply preset color mask on the default FieldHRM logos (SVGs shipped with the app).
  // If a super admin uploaded a custom logo via siteSettings, show it as-is without recoloring.
  const isDefaultLogo = logoSrc === '/logo/logo_full.svg' || logoSrc === '/logo/logo_single.svg';

  // Layered approach:
  // Layer 1 (bottom): Original <img> as fallback (always visible behind)
  // Layer 2 (top): Solid primary-color block masked by logo shape (only for default FieldHRM logo)
  const logo = (
    <Box
      ref={ref}
      sx={{
        width: logoWidth,
        height: logoHeight,
        cursor: 'pointer',
        display: 'inline-flex',
        position: 'relative',
        flexShrink: 0,
        ...sx,
      }}
      {...other}
    >
      {/* Layer 1: Fallback — original image (shows if mask fails or for custom logos) */}
      <Box
        component="img"
        src={logoSrc}
        alt={siteSettings?.name || 'Logo'}
        onError={(e) => {
          e.target.src = variant === 'small'
            ? '/logo/logo_single.svg'
            : '/logo/logo_full.svg';
        }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
      />

      {/* Layer 2: Primary-colored mask overlay — ONLY for default FieldHRM logo */}
      {isDefaultLogo && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: primaryColor,
            WebkitMaskImage: `url(${logoSrc})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url(${logoSrc})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            transition: 'background-color 0.3s ease',
          }}
        />
      )}
    </Box>
  );

  if (disabledLink) {
    return logo;
  }

  return (
    <Link component={RouterLink} href="/" sx={{ display: 'contents' }}>
      {logo}
    </Link>
  );
});

Logo.propTypes = {
  disabledLink: PropTypes.bool,
  variant: PropTypes.oneOf(['full', 'small']),
  sx: PropTypes.object,
};

export default Logo;
